import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, CalendarClock, Check } from "lucide-react";
import { api, errMsg, fmtDate } from "@/lib/api";
import { Brand } from "@/components/Shell";
import { Button } from "@/components/ui/button";

export default function ReschedulePage() {
  const { aid } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [pick, setPick] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/appointments/${aid}/next-slots`).then(({ data: d }) => setData(d)).catch((e) => setError(errMsg(e)));
  }, [aid]);

  const confirm = async () => {
    setBusy(true);
    try {
      await api.post(`/appointments/${aid}/reschedule`, { date: pick.date, time: pick.time, note: "Riprogrammato dal paziente dopo il ritardo del professionista" });
      toast.success(`Consulto spostato al ${fmtDate(pick.date)} alle ${pick.time}`);
      navigate("/area-personale");
    } catch (e) {
      toast.error(errMsg(e));
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-emerald-50/40 px-4 py-6" data-testid="reschedule-page">
      <div className="mx-auto max-w-lg">
        <div className="mb-6 flex items-center gap-3"><Link to="/area-personale" data-testid="reschedule-back" className="grid h-10 w-10 place-items-center rounded-full bg-white text-slate-700 shadow-sm"><ArrowLeft className="h-4 w-4" /></Link><Brand /></div>
        <h1 className="font-display text-3xl font-extrabold text-slate-900">Scegli un nuovo orario</h1>
        {error && <p className="mt-4 rounded-xl bg-rose-50 p-3 text-sm text-rose-700" data-testid="reschedule-error">{error}</p>}
        {data && (
          <>
            <p className="mt-2 text-sm text-slate-600" data-testid="reschedule-current">{data.appointment.service_name} con {data.appointment.professional_name} · previsto il {fmtDate(data.appointment.date)} alle {data.appointment.time}</p>
            {!data.slots.length && <p className="mt-6 text-sm text-slate-600" data-testid="reschedule-empty">Nessun orario libero nelle prossime 3 settimane: la segreteria ti contatterà.</p>}
            <div className="mt-6 grid gap-2 sm:grid-cols-2">
              {data.slots.map((s) => {
                const on = pick?.date === s.date && pick?.time === s.time;
                return (
                  <button key={`${s.date}${s.time}`} data-testid={`reschedule-slot-${s.date}-${s.time}`} onClick={() => setPick(s)}
                    className={`flex items-center gap-3 rounded-2xl border p-4 text-left transition-colors ${on ? "border-emerald-600 bg-emerald-600 text-white" : "border-slate-200 bg-white text-slate-800 hover:border-emerald-400"}`}>
                    {on ? <Check className="h-5 w-5" /> : <CalendarClock className="h-5 w-5 text-emerald-600" />}
                    <span><span className="block text-sm font-bold">{fmtDate(s.date)}</span><span className="text-xs opacity-80">ore {s.time}</span></span>
                  </button>
                );
              })}
            </div>
            <Button data-testid="reschedule-confirm" disabled={!pick || busy} onClick={confirm} className="mt-6 w-full rounded-full bg-emerald-600 py-6 text-base font-bold hover:bg-emerald-700">
              {pick ? `Conferma ${fmtDate(pick.date)} alle ${pick.time}` : "Tocca un orario"}
            </Button>
            <p className="mt-3 text-xs text-slate-500">Il pagamento resta valido per il nuovo orario. Riceverai il promemoria 15 minuti prima.</p>
          </>
        )}
      </div>
    </div>
  );
}
